import clsx from "clsx";
import { DateFormat, formatDate, parseTime, toLocalDate } from "common";
import { Dayjs } from "dayjs";
import { memoize, uniqBy } from "lodash";
import { useMemo } from "react";
import { useQueryClient } from "react-query";
import { Event, Experience, Timeslot } from "types";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardMode, DashboardScope } from "@/types/Dashboard";

interface CalendarEventsProps {
    timeslots: Timeslot[];
    dashboardMode: DashboardMode;
    selectedTimeslotIds: Set<string>;
    selectedEvents: Set<Event>;
    currentDate: Dayjs;
    isTimeslotError: boolean;
}

export const useCalendarEvents = ({
    timeslots,
    dashboardMode,
    selectedTimeslotIds,
    selectedEvents,
    currentDate,
    isTimeslotError,
}: CalendarEventsProps) => {
    const queryClient = useQueryClient();
    const isMonthMode = dashboardMode === DashboardMode.MONTH;

    const { filters } = useDashboardFilterStore((state) => state);

    const getExperience = memoize((productId) => {
        return queryClient.getQueryData<Experience>(["products", productId]);
    });

    const getEvent = memoize((eventId) => {
        return queryClient.getQueryData<Event>(["events", eventId]);
    });

    const getDateTime = memoize((timeslot: Timeslot) => {
        // FullCalendar expects start and end dates to be in local timezone
        // Using `timeslot.date` string for all day events to ensure that time and timezone are ignored
        const { hours, minutes } = timeslot.time ? parseTime(timeslot.time) : { hours: 0, minutes: 0 };
        const allDay = timeslot.time === null;

        const start =
            allDay || isMonthMode
                ? timeslot.date
                : toLocalDate(timeslot.arrivalDatetime).set("hours", hours).set("minutes", minutes).format();
        const end =
            allDay && !timeslot.end
                ? timeslot.date
                : isMonthMode || allDay // Add 1 day so that end date is included by fullCalendar in month view or all Day view
                ? toLocalDate(timeslot.end).add(1, "day").format()
                : toLocalDate(timeslot.end).format();

        return { start, end };
    });

    const filteredTimeslots =
        filters.scope === DashboardScope.RESERVED
            ? timeslots.filter((timeslot) => timeslot.capacity.reserved !== 0)
            : timeslots;

    return useMemo(() => {
        if (isTimeslotError) return [];

        return uniqBy(filteredTimeslots, "id").map((timeslot) => {
            const experience = getExperience(timeslot.product);
            const event = getEvent(timeslot.event?.id);
            const allDay = timeslot.time === null;
            const selectedEvent = event && isEventSelected(event, selectedEvents) ? event : null;
            const { start, end } = getDateTime(timeslot);

            const eventObject: EventObject = {
                type: EventType.TIMESLOT,
                title: experience?.name,
                start,
                end,
                allDay: dashboardMode === DashboardMode.MONTH ? true : allDay,
                className: clsx(
                    dashboardMode === DashboardMode.DAY ? "w-full" : "",
                    selectedEvent || selectedTimeslotIds.has(timeslot.id) ? "selected" : "",
                ),
                event,
                selectedEvent,
                experience,
                timeslot,
                showPopover: false,
            };

            if (dashboardMode === DashboardMode.DAY) {
                eventObject.resourceId = formatDate(currentDate, DateFormat.DATE_ISO);
            } else if (event?.resourceUsages) {
                eventObject.resourceIds = event.resourceUsages
                    .filter((resourceUsage) => resourceUsage.count !== 0)
                    .map((resourceUsage) => resourceUsage.resource.id);
            }

            return eventObject;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeslots, dashboardMode, selectedTimeslotIds, selectedEvents, currentDate]);
};

export interface EventObject {
    type: EventType;
    title?: string;
    start?: string;
    end?: string;
    allDay: boolean;
    className: string;
    event?: Event;
    selectedEvent: Event | null;
    experience?: Experience;
    timeslot: Timeslot;
    showPopover: boolean;
    resourceId?: string;
    resourceIds?: string[];
}

export enum EventType {
    TIMESLOT = "timeslot",
    RESTRICTED = "restricted",
}

const isEventSelected = (event: Event, selectedEvents: Set<Event>) => {
    for (const selectedEvent of selectedEvents) {
        if (selectedEvent.id === event.id) {
            return true; // Event with given ID is in the set
        }
    }

    return false;
};
